import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiService } from '../../js/ApiService';
import useClient from '../util/useClient/useClient';
import SafeHtmlError from '../util/SafeHtmlError/SafeHtmlError';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CheckWBG from './assets/check-whitebg.svg';
import InputField from '../util/InputField/InputField';
import RegisterReCaptcha from '../ReCaptcha/RegisterReCaptcha/RegisterReCaptcha';

const schema = z.object({
  username: z.string().email({ message: 'Please enter a valid email address' })
});

function ResetPasswordPrompt() {
  const {
    register,
    errors,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm({ resolver: zodResolver(schema), mode: 'onChange' });
  const client = useClient();
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [reCaptchaToken, setreCaptchaToken] = useState(null);

  useEffect(() => {
    const handleToken = ev => {
      setreCaptchaToken(ev.token);
      setMessage('');
    };
    document.addEventListener('rTokenRdy', handleToken, false);
    return () => {
      document.removeEventListener('rTokenRdy', handleToken, false);
    };
  }, []);

  useEffect(() => {
    document.title = `Reset Password | ${client.startsWith('finl') ? 'Finish Line' : 'JD Sports'}`;
  }, [client]);

  const onSubmit = async data => {
    if (!reCaptchaToken) {
      setMessage('You need to complete reCaptcha');
      return;
    }
    const payload = {
      email: data.username,
      clientId: client,
      recaptchaToken: reCaptchaToken
    };
    setreCaptchaToken(null);
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      try {
        window.grecaptcha.enterprise.reset();
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const res = await ApiService.requestResetPassword(payload);
      if (res.status === 200) {
        setIsSuccess(true);
        return;
      }
      const msg = await res.text();
      setMessage(msg);
    } catch {
      setMessage('An error occurred');
    }
  };

  return (
    <div className="body bg-white">
      {!isSuccess && (
        <div>
          <div className="d-flex flex-column align-items-center justify-content-center banner">
            <h1 className="h1 mb-4 text-center" style={{ marginTop: '28px' }}>
              RESET PASSWORD
            </h1>
            <div className="text-left mb-4" style={{ lineHeight: '20px' }}>
              <span>
                Enter your email address below and we&apos;ll send you a link to reset your
                password.
              </span>
            </div>
          </div>
          <div className="form">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-3 text-left">
                <InputField
                  name="username"
                  className="form-input"
                  type="email"
                  placeholder="Email"
                  aria-describedby="Email input"
                  aria-labelledby="username"
                  ref={register()}
                />
                {errors.username && (
                  <div className="form-text text-danger" role="alert">
                    {errors.username.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <RegisterReCaptcha action="FORGOT_PASSWORD" />
              </div>

              <div className="text-center my-4">
                <input
                  disabled={isSubmitting || !isValid}
                  type="submit"
                  className="btn btn-primary btn-block"
                  value="CONTINUE"
                />
              </div>
            </form>
            <SafeHtmlError message={message} className="text-danger text-center" />
          </div>
        </div>
      )}
      {isSuccess && (
        <div>
          <div className="d-flex flex-column align-items-center justify-content-center banner">
            <div className="mt-4">
              <img src={CheckWBG} alt="Success Check" />
            </div>
            <h1 className="h1 mb-4 text-center" style={{ marginTop: '28px' }}>
              RESET PASSWORD
            </h1>
            <div className="text-left mb-4" style={{ lineHeight: '20px' }}>
              <span>
                If this email is in our system, we&apos;ll send you an email with a link to reset
                your password.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResetPasswordPrompt;
